import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import { toast } from 'sonner';

const TranslationContext = createContext();

// Cache for translations to avoid repeated API calls
const translationCache = new Map();

// Batch translation queue
let batchQueue = [];
let batchTimeout = null;

// Track if we've shown the error toast to avoid spamming
let hasShownApiError = false;

export const TranslationProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [isEnabled, setIsEnabled] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);
  const [translationError, setTranslationError] = useState(null);
  const [reservedWords, setReservedWords] = useState([]);
  const observerRef = useRef(null);
  const translatedNodesRef = useRef(new WeakMap());

  // Check if translation is enabled and load saved language + reserved words
  useEffect(() => {
    const checkTranslationStatus = async () => {
      try {
        const res = await api.get('/integrations/google-translate/status');
        setIsEnabled(res.data.enabled);
        
        if (res.data.enabled) {
          const savedLang = localStorage.getItem('preferred_language') || 'en';
          setCurrentLanguage(savedLang);
          
          // Load reserved words
          try {
            const reservedRes = await api.get('/integrations/google-translate/reserved-words');
            setReservedWords(reservedRes.data.reserved_words || []);
          } catch (e) {
            // Reserved words feature may not exist yet
          }
        }
      } catch (error) {
        console.log('Translation service not available');
      }
    };
    checkTranslationStatus();
  }, []);

  // Reset error flag when language changes
  useEffect(() => {
    hasShownApiError = false;
    setTranslationError(null);
  }, [currentLanguage]);

  // Listen for language change events from LanguageSelector
  useEffect(() => {
    const handleLanguageChange = (event) => {
      const newLang = event.detail.language;
      setCurrentLanguage(newLang);
      localStorage.setItem('preferred_language', newLang);
      
      // Translate the page when language changes
      if (newLang !== 'en') {
        translatePage(newLang);
      } else {
        restoreOriginalText();
      }
    };

    window.addEventListener('languageChange', handleLanguageChange);
    return () => window.removeEventListener('languageChange', handleLanguageChange);
  }, []);

  // Translate text using the API
  const translateText = useCallback(async (text, targetLang) => {
    if (!text || !text.trim() || targetLang === 'en') {
      return text;
    }

    // Check cache first
    const cacheKey = `${targetLang}:${text}`;
    if (translationCache.has(cacheKey)) {
      return translationCache.get(cacheKey);
    }

    try {
      const res = await api.post('/integrations/translate', {
        text: text,
        target_language: targetLang,
        source_language: 'en'
      });
      
      const translated = res.data.translatedText;
      translationCache.set(cacheKey, translated);
      setTranslationError(null);
      return translated;
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Translation failed';
      setTranslationError(errorMessage);
      
      // Show toast only once per session to avoid spamming
      if (!hasShownApiError) {
        hasShownApiError = true;
        if (errorMessage.includes('API key')) {
          toast.error('Translation API key is invalid. Please check your settings.', {
            duration: 8000,
            id: 'translation-error'
          });
        } else {
          toast.error(`Translation error: ${errorMessage}`, {
            duration: 5000,
            id: 'translation-error'
          });
        }
      }
      return text; // Return original on error
    }
  }, []);

  // Batch translate multiple texts
  const batchTranslate = useCallback(async (texts, targetLang) => {
    if (!texts.length || targetLang === 'en') {
      return texts;
    }

    // Filter out cached and empty texts
    const uncachedTexts = [];
    const results = new Array(texts.length);
    
    texts.forEach((text, index) => {
      if (!text || !text.trim()) {
        results[index] = text;
      } else {
        const cacheKey = `${targetLang}:${text}`;
        if (translationCache.has(cacheKey)) {
          results[index] = translationCache.get(cacheKey);
        } else {
          uncachedTexts.push({ text, index });
        }
      }
    });

    if (uncachedTexts.length === 0) {
      return results;
    }

    // Translate texts individually in small batches to avoid rate limits
    // This is more reliable than using delimiters which get translated
    const batchSize = 10; // Translate 10 at a time in parallel
    
    for (let i = 0; i < uncachedTexts.length; i += batchSize) {
      const batch = uncachedTexts.slice(i, i + batchSize);
      
      try {
        // Translate each text individually in parallel
        const translationPromises = batch.map(async (item) => {
          try {
            const res = await api.post('/integrations/translate', {
              text: item.text,
              target_language: targetLang,
              source_language: 'en'
            });
            return { 
              index: item.index, 
              text: item.text,
              translated: res.data.translatedText 
            };
          } catch (err) {
            // On error, return original text
            return { 
              index: item.index, 
              text: item.text,
              translated: item.text 
            };
          }
        });
        
        const translations = await Promise.all(translationPromises);
        
        translations.forEach(({ index, text, translated }) => {
          const cacheKey = `${targetLang}:${text}`;
          translationCache.set(cacheKey, translated);
          results[index] = translated;
        });
      } catch (error) {
        const errorMessage = error.response?.data?.detail || 'Batch translation error';
        setTranslationError(errorMessage);
        
        // Show toast only once per session
        if (!hasShownApiError) {
          hasShownApiError = true;
          if (errorMessage.includes('API key')) {
            toast.error('Translation API key is invalid. Please check your settings.', {
              duration: 8000,
              id: 'translation-error'
            });
          } else {
            toast.error(`Translation error: ${errorMessage}`, {
              duration: 5000,
              id: 'translation-error'
            });
          }
        }
        // On error, use original texts
        batch.forEach(item => {
          results[item.index] = item.text;
        });
      }
    }

    return results;
  }, []);

  // Check if a node should be translated
  const shouldTranslateNode = (node) => {
    if (!node) return false;
    
    // Check if it's a text node with actual text
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent.trim();
      if (!text || text.length < 2) return false;
      // Skip if mostly numbers/symbols
      if (/^[\d\s\-\+\.\,\:\;\!\?\@\#\$\%\^\&\*\(\)\/\\]+$/.test(text)) return false;
      
      // Check if text exactly matches a reserved word (case insensitive)
      if (reservedWords.some(word => text.toLowerCase() === word.toLowerCase())) {
        return false;
      }
      
      // Skip email addresses
      if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(text)) return false;
      
      // Skip URLs
      if (/^https?:\/\//.test(text) || /^www\./.test(text)) return false;
      
      // Skip UUIDs and IDs (like MongoDB ObjectIds)
      if (/^[a-f0-9]{24}$/i.test(text)) return false;
      if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(text)) return false;
      
      // Skip IP addresses
      if (/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}/.test(text)) return false;
      if (/^[0-9a-f:]+$/i.test(text) && text.includes(':')) return false; // IPv6
      
      // Skip dates in various formats
      if (/^\d{1,2}[\/-]\d{1,2}[\/-]\d{2,4}/.test(text)) return false;
      if (/^(January|February|March|April|May|June|July|August|September|October|November|December|Jan|Feb|Mar|Apr|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+\d{1,2}/i.test(text)) return false;
      
      // Skip if parent has data-value or is displaying dynamic data
      const parent = node.parentElement;
      if (parent) {
        // Skip if parent is a data display element
        if (parent.getAttribute && (
          parent.getAttribute('data-value') ||
          parent.getAttribute('data-user-data') ||
          parent.getAttribute('data-no-translate') ||
          parent.classList?.contains('user-data') ||
          parent.classList?.contains('data-value')
        )) {
          return false;
        }
      }
      
      return true;
    }
    
    return false;
  };

  // Check if element should be excluded from translation
  const isExcludedElement = (element) => {
    if (!element || !element.tagName) return false;
    
    // Skip these tags
    const excludedTags = ['SCRIPT', 'STYLE', 'CODE', 'PRE', 'TEXTAREA', 'INPUT', 'SELECT', 'OPTION', 'NOSCRIPT'];
    if (excludedTags.includes(element.tagName)) return true;
    
    // Skip elements with translate="no" or class="notranslate"
    if (element.getAttribute && element.getAttribute('translate') === 'no') return true;
    if (element.classList && element.classList.contains('notranslate')) return true;
    
    // Skip data-testid elements (usually technical identifiers)
    if (element.getAttribute && element.getAttribute('data-testid')) {
      const testId = element.getAttribute('data-testid');
      if (testId.includes('input') || testId.includes('btn') || testId.includes('select')) {
        // These are interactive elements, check their labels not values
      }
    }
    
    // Check parent elements
    let parent = element.parentElement;
    while (parent) {
      if (parent.getAttribute && parent.getAttribute('translate') === 'no') return true;
      if (parent.classList && parent.classList.contains('notranslate')) return true;
      parent = parent.parentElement;
    }
    
    return false;
  };

  // Get all text nodes in an element
  const getTextNodes = (element) => {
    const textNodes = [];
    const walker = document.createTreeWalker(
      element,
      NodeFilter.SHOW_TEXT,
      {
        acceptNode: (node) => {
          if (!shouldTranslateNode(node)) return NodeFilter.FILTER_REJECT;
          if (isExcludedElement(node.parentElement)) return NodeFilter.FILTER_REJECT;
          return NodeFilter.FILTER_ACCEPT;
        }
      }
    );

    while (walker.nextNode()) {
      textNodes.push(walker.currentNode);
    }

    return textNodes;
  };

  // Translate the entire page
  const translatePage = useCallback(async (targetLang) => {
    if (!isEnabled || targetLang === 'en') return;
    
    setIsTranslating(true);
    
    try {
      const textNodes = getTextNodes(document.body);
      const textsToTranslate = [];
      const nodesToUpdate = [];

      textNodes.forEach(node => {
        // Store original text if not already stored
        if (!translatedNodesRef.current.has(node)) {
          translatedNodesRef.current.set(node, node.textContent);
        }
        
        const originalText = translatedNodesRef.current.get(node);
        if (originalText && originalText.trim()) {
          textsToTranslate.push(originalText);
          nodesToUpdate.push(node);
        }
      });

      if (textsToTranslate.length > 0) {
        const translated = await batchTranslate(textsToTranslate, targetLang);
        
        nodesToUpdate.forEach((node, index) => {
          if (translated[index] && node.parentNode) {
            node.textContent = translated[index];
          }
        });
      }
    } catch (error) {
      console.error('Page translation error:', error);
    } finally {
      setIsTranslating(false);
    }
  }, [isEnabled, batchTranslate]);

  // Restore original text
  const restoreOriginalText = useCallback(() => {
    const textNodes = getTextNodes(document.body);
    textNodes.forEach(node => {
      const original = translatedNodesRef.current.get(node);
      if (original) {
        node.textContent = original;
      }
    });
  }, []);

  // Set up MutationObserver to translate new content
  useEffect(() => {
    if (!isEnabled || currentLanguage === 'en') return;

    const translateNewNodes = async (mutations) => {
      const newTextNodes = [];
      
      mutations.forEach(mutation => {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const textNodes = getTextNodes(node);
            newTextNodes.push(...textNodes);
          } else if (node.nodeType === Node.TEXT_NODE && shouldTranslateNode(node)) {
            if (!isExcludedElement(node.parentElement)) {
              newTextNodes.push(node);
            }
          }
        });
      });

      if (newTextNodes.length > 0) {
        const textsToTranslate = [];
        const nodesToUpdate = [];

        newTextNodes.forEach(node => {
          if (!translatedNodesRef.current.has(node)) {
            translatedNodesRef.current.set(node, node.textContent);
          }
          const originalText = translatedNodesRef.current.get(node);
          if (originalText && originalText.trim()) {
            textsToTranslate.push(originalText);
            nodesToUpdate.push(node);
          }
        });

        if (textsToTranslate.length > 0) {
          const translated = await batchTranslate(textsToTranslate, currentLanguage);
          nodesToUpdate.forEach((node, index) => {
            if (translated[index] && node.parentNode) {
              node.textContent = translated[index];
            }
          });
        }
      }
    };

    // Debounce observer callback
    let timeout;
    const debouncedTranslate = (mutations) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => translateNewNodes(mutations), 100);
    };

    observerRef.current = new MutationObserver(debouncedTranslate);
    observerRef.current.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Initial translation
    translatePage(currentLanguage);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      clearTimeout(timeout);
    };
  }, [isEnabled, currentLanguage, translatePage, batchTranslate]);

  // Function to refresh reserved words from server
  const refreshReservedWords = useCallback(async () => {
    try {
      const reservedRes = await api.get('/integrations/google-translate/reserved-words');
      setReservedWords(reservedRes.data.reserved_words || []);
    } catch (e) {
      console.log('Could not fetch reserved words');
    }
  }, []);

  const value = {
    currentLanguage,
    isEnabled,
    isTranslating,
    translationError,
    reservedWords,
    translateText,
    batchTranslate,
    translatePage,
    restoreOriginalText,
    refreshReservedWords
  };

  return (
    <TranslationContext.Provider value={value}>
      {children}
      {/* Translation loading indicator */}
      {isTranslating && (
        <div className="fixed bottom-4 right-4 bg-blue-600 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2 z-50">
          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
          <span className="text-sm">Translating...</span>
        </div>
      )}
    </TranslationContext.Provider>
  );
};

export const useTranslation = () => {
  const context = useContext(TranslationContext);
  if (!context) {
    throw new Error('useTranslation must be used within a TranslationProvider');
  }
  return context;
};

export default TranslationContext;
